export type FormulaRounding = 'none' | 'nearest' | 'up' | 'down';

export interface FormulaEditorValue {
  body: string;
  rounding: FormulaRounding;
  decimals: number;
}

const stripEquals = (value: string): string => value.trim().replace(/^=/, '').trim();

/**
 * Leest alleen een functie die de volledige formule omhult. Zo kunnen we een
 * bestaande ROUND(SUM(a,b), 1) veilig terugbrengen naar de visuele instellingen
 * zonder geneste functies of komma's in SUM als argumentgrens te verwarren.
 */
function outerFunction(value: string): { name: string; args: string[] } | null {
  const open = value.indexOf('(');
  if (open <= 0 || !value.endsWith(')')) return null;

  const name = value.slice(0, open).trim().toUpperCase();
  if (!/^[A-Z]+$/.test(name)) return null;

  let depth = 0;
  for (let index = open; index < value.length; index += 1) {
    if (value[index] === '(') depth += 1;
    if (value[index] === ')') depth -= 1;
    if (depth < 0 || (depth === 0 && index < value.length - 1)) return null;
  }
  if (depth !== 0) return null;

  const content = value.slice(open + 1, -1);
  const args: string[] = [];
  let start = 0;
  depth = 0;
  for (let index = 0; index < content.length; index += 1) {
    if (content[index] === '(') depth += 1;
    if (content[index] === ')') depth -= 1;
    if ((content[index] === ',' || content[index] === ';') && depth === 0) {
      args.push(content.slice(start, index).trim());
      start = index + 1;
    }
  }
  args.push(content.slice(start).trim());

  return { name, args };
}

export function parseFormulaEditorValue(value: string): FormulaEditorValue {
  const body = stripEquals(value);
  const call = outerFunction(body);

  if (call?.name === 'ROUND' && (call.args.length === 1 || call.args.length === 2)) {
    const decimals = call.args.length === 2 ? Number(call.args[1]) : 0;
    if (call.args[0] && Number.isInteger(decimals) && decimals >= 0 && decimals <= 6) {
      return { body: call.args[0], rounding: 'nearest', decimals };
    }
  }

  if (call?.name === 'CEIL' && call.args.length === 1 && call.args[0]) {
    return { body: call.args[0], rounding: 'up', decimals: 0 };
  }

  if (call?.name === 'FLOOR' && call.args.length === 1 && call.args[0]) {
    return { body: call.args[0], rounding: 'down', decimals: 0 };
  }

  return { body, rounding: 'none', decimals: 0 };
}

/**
 * Zet decimale komma's (0,10) om naar punten (0.10), met dezelfde regels als de
 * rekenmachine: alleen tussen cijfers en niet binnen de argumenten van een
 * functie. Nodig voordat we er een afrondingsfunctie omheen zetten, want binnen
 * CEIL( … ) leest 0,10 anders als twee waarden.
 */
export function normalizeDecimalCommas(expression: string): string {
  const parens: Array<'func' | 'group'> = [];
  let result = '';
  for (let index = 0; index < expression.length; index += 1) {
    const char = expression[index];
    if (char === '(') {
      parens.push(/[A-Za-z_][A-Za-z0-9_]*\s*$/.test(result) ? 'func' : 'group');
    } else if (char === ')') {
      parens.pop();
    } else if (
      char === ',' &&
      parens[parens.length - 1] !== 'func' &&
      /(?:^|[^A-Za-z0-9_])\d+$/.test(result) &&
      /[0-9]/.test(expression[index + 1] ?? '')
    ) {
      result += '.';
      continue;
    }
    result += char;
  }
  return result;
}

export function buildFormulaEditorValue(
  body: string,
  rounding: FormulaRounding,
  decimals = 0
): string {
  const expression = normalizeDecimalCommas(stripEquals(body));
  if (!expression) return '';

  switch (rounding) {
    case 'nearest':
      return decimals > 0
        ? `=ROUND(${expression},${Math.max(0, Math.trunc(decimals))})`
        : `=ROUND(${expression})`;
    case 'up':
      return `=CEIL(${expression})`;
    case 'down':
      return `=FLOOR(${expression})`;
    default:
      return `=${expression}`;
  }
}
